// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineContext } from '@/contexts/timeline-context';
import { alpha, colors, useTheme } from '@mui/material';
import { PatternLines } from '@visx/pattern';
import { PropsWithChildren, useId } from 'react';

/**
 * The default TimelineContext provider
 */
export function TimelineProvider(props: PropsWithChildren) {
  const theme = useTheme();
  const assetPatternId = useId();
  const focusPatternId = useId();
  const invalidPatternId = useId();
  const projectPatternId = useId();
  const disabledPatternId = useId();
  const assignmentPatternId = useId();

  const palette = {
    asset: {
      solid: theme.palette.grey[400],
      diagonal: `url(#${assetPatternId})`,
    },
    focus: {
      solid: alpha(colors.yellow[200], 0.25),
      diagonal: `url(#${focusPatternId})`,
    },
    invalid: {
      solid: theme.palette.error.main,
      diagonal: `url(#${invalidPatternId})`,
    },
    project: {
      solid: theme.palette.primary.main,
      diagonal: `url(#${projectPatternId})`,
    },
    disabled: {
      solid: theme.palette.text.disabled,
      diagonal: `url(#${disabledPatternId})`,
    },
    assignment: {
      solid: theme.palette.primary.light,
      diagonal: `url(#${assignmentPatternId})`,
    },
  };

  return (
    <TimelineContext.Provider value={{ palette }}>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <PatternLines
          id={assetPatternId}
          height={4}
          width={4}
          strokeWidth={1.5}
          stroke={alpha(palette.asset.solid, 0.75)}
          background={alpha(palette.asset.solid, 0.2)}
          orientation={['diagonal']}
        />
        <PatternLines
          id={focusPatternId}
          height={4}
          width={4}
          strokeWidth={1.5}
          stroke={alpha(palette.focus.solid, 0.75)}
          background={alpha(palette.focus.solid, 0.2)}
          orientation={['diagonal']}
        />
        <PatternLines
          id={invalidPatternId}
          height={4}
          width={4}
          strokeWidth={1.5}
          stroke={alpha(palette.invalid.solid, 0.75)}
          background={alpha(palette.invalid.solid, 0.2)}
          orientation={['diagonal']}
        />
        <PatternLines
          id={projectPatternId}
          height={4}
          width={4}
          strokeWidth={1.5}
          stroke={alpha(palette.project.solid, 0.75)}
          background={alpha(palette.project.solid, 0.2)}
          orientation={['diagonal']}
        />
        <PatternLines
          id={disabledPatternId}
          height={4}
          width={4}
          strokeWidth={1.5}
          stroke={alpha(palette.disabled.solid, 0.75)}
          background={alpha(palette.disabled.solid, 0.2)}
          orientation={['diagonal']}
        />
        <PatternLines
          id={assignmentPatternId}
          height={4}
          width={4}
          strokeWidth={1.5}
          stroke={alpha(palette.assignment.solid, 0.75)}
          background={alpha(palette.assignment.solid, 0.2)}
          orientation={['diagonal']}
        />
      </svg>
      {props.children}
    </TimelineContext.Provider>
  );
}
